import Fuse from "fuse.js";
import { AppleStoreType, appleStores } from "./appleStores";

// Multilingual keyword mapping for Apple Store search
export const multilingualKeywords: Record<string, string[]> = {
  // Store-related terms
  "store": ["negozio", "boutique", "tienda", "laden", "магазин", "店", "商店", "loja", "winkel", "butik", "geschäft", "magazin"],
  "negozio": ["store", "boutique", "tienda", "laden", "shop", "geschäft"],
  "boutique": ["store", "negozio", "tienda", "shop"],
  "shop": ["store", "negozio", "boutique", "tienda", "laden", "geschäft"],
  "geschäft": ["store", "negozio", "shop", "laden"],
  
  // City translations - Extended
  "milan": ["milano", "mailand"],
  "milano": ["milan", "mailand"],
  "rome": ["roma", "rom"],
  "roma": ["rome", "rom"],
  "florence": ["firenze", "florenz"],
  "firenze": ["florence", "florenz"],
  "venice": ["venezia", "venedig"],
  "venezia": ["venice", "venedig"],
  "naples": ["napoli", "neapel"],
  "napoli": ["naples", "neapel"],
  "turin": ["torino", "turijn"],
  "torino": ["turin", "turijn"],
  "london": ["londra", "londres", "londyn"],
  "londra": ["london", "londres"],
  "paris": ["parigi", "parís"],
  "parigi": ["paris", "parís"],
  "munich": ["münchen", "monaco di baviera", "munchen"],
  "münchen": ["munich", "monaco di baviera"],
  "berlin": ["berlino", "berlín"],
  "berlino": ["berlin", "berlín"],
  "madrid": ["madri"],
  "barcelona": ["barcellona", "barcelone"],
  "barcellona": ["barcelona", "barcelone"],
  "new york": ["nuova york", "nueva york", "nova iorque", "ny"],
  "ny": ["new york", "nuova york"],
  "los angeles": ["los ángeles", "la"],
  "la": ["los angeles"],
  "san francisco": ["sf"],
  "sf": ["san francisco"],
  "chicago": ["chicago"],
  "tokyo": ["tokio", "東京"],
  "tokio": ["tokyo", "東京"],
  "beijing": ["pechino", "peking", "北京"],
  "pechino": ["beijing", "peking"],
  "shanghai": ["上海"],
  "hong kong": ["香港"],
  "sydney": ["sidney"],
  "amsterdam": ["amsterdam"],
  "vienna": ["wien", "vienne"],
  "wien": ["vienna", "vienne"],
  "zurich": ["zurigo", "zürich"],
  "zurigo": ["zurich", "zürich"],
  "geneva": ["ginevra", "genf", "genève"],
  "ginevra": ["geneva", "genf"],
  "brussels": ["bruxelles", "brussel", "brüssel"],
  "bruxelles": ["brussels", "brussel"],
  "dubai": ["دبي"],
  "singapore": ["singapura", "singapur", "新加坡"],
  "seoul": ["seul", "서울"],
  "cupertino": ["cupertino", "apple park"],
  
  // Country translations
  "usa": ["stati uniti", "united states", "eeuu", "états-unis", "america", "estados unidos"],
  "stati uniti": ["usa", "united states", "eeuu", "america"],
  "united states": ["usa", "stati uniti", "eeuu", "america"],
  "america": ["usa", "stati uniti", "united states"],
  "italy": ["italia", "italien", "italie"],
  "italia": ["italy", "italien", "italie"],
  "germany": ["germania", "deutschland", "allemagne"],
  "germania": ["germany", "deutschland"],
  "france": ["francia", "frankreich"],
  "francia": ["france", "frankreich"],
  "spain": ["spagna", "españa", "spanien"],
  "spagna": ["spain", "españa"],
  "uk": ["regno unito", "united kingdom", "gran bretagna", "inghilterra", "england"],
  "regno unito": ["uk", "united kingdom", "great britain", "england"],
  "united kingdom": ["uk", "regno unito", "great britain", "england"],
  "england": ["uk", "regno unito", "inghilterra"],
  "inghilterra": ["uk", "england", "united kingdom"],
  "japan": ["giappone", "japón", "japon"],
  "giappone": ["japan", "japón"],
  "china": ["cina", "中国"],
  "cina": ["china", "中国"],
  "australia": ["australie"],
  "canada": ["canadá"],
  "netherlands": ["olanda", "paesi bassi", "nederland"],
  "olanda": ["netherlands", "paesi bassi"],
  "belgium": ["belgio", "belgique", "belgië"],
  "belgio": ["belgium", "belgique"],
  "switzerland": ["svizzera", "schweiz", "suisse"],
  "svizzera": ["switzerland", "schweiz", "suisse"],
  "austria": ["österreich", "autriche"],
  "sweden": ["svezia", "sverige", "schweden"],
  "svezia": ["sweden", "sverige"],
  
  // Service-related terms
  "repair": ["riparazione", "reparación", "reparatur", "réparation"],
  "riparazione": ["repair", "reparación", "reparatur"],
  "support": ["assistenza", "supporto", "soporte", "unterstützung"],
  "assistenza": ["support", "supporto", "assistance"],
  "genius": ["genius bar", "bar del genio"],
  "training": ["formazione", "corso", "training"],
  "formazione": ["training", "corso"],
  "workshop": ["laboratorio", "atelier"],
  
  // Common search terms
  "apple": ["mela", "apple store"],
  "mela": ["apple"],
  "iphone": ["iphone"],
  "mac": ["mac", "macbook"],
  "ipad": ["ipad"],
  "watch": ["orologio", "apple watch"],
  "orologio": ["watch", "apple watch"],
  
  // Directional/location terms
  "near": ["vicino", "cerca", "proche", "nahe", "nearby"],
  "vicino": ["near", "nearby", "proche"],
  "nearby": ["near", "vicino", "proche"],
  "centro": ["center", "centre", "zentrum", "downtown"],
  "center": ["centro", "centre", "downtown"],
  "centre": ["centro", "center", "downtown"],
  "downtown": ["centro città", "centro storico", "center"],
  "piazza": ["square", "plaza", "platz"],
  "square": ["piazza", "plaza", "platz"],
  "plaza": ["piazza", "square", "platz"],
  "avenue": ["viale", "avenida", "allee"],
  "viale": ["avenue", "avenida"],
  "street": ["via", "strada", "calle", "strasse"],
  "via": ["street", "strada"],
  "strada": ["street", "via"],
  
  // Common store name patterns
  "fifth avenue": ["quinta strada", "5th avenue"],
  "quinta strada": ["fifth avenue", "5th avenue"],
  "grand central": ["stazione centrale"],
  "fashion": ["moda"],
  "mall": ["centro commerciale", "centro"],
  "centro commerciale": ["mall", "shopping center"],
};

// Function to expand search query with synonyms
export const expandSearchQuery = (query: string): string[] => {
  const normalizedQuery = query.toLowerCase().trim();
  const words = normalizedQuery.split(/\s+/);
  const expandedTerms = new Set<string>([normalizedQuery]);
  
  // Add each word and its synonyms
  words.forEach(word => {
    expandedTerms.add(word);
    
    // Find synonyms in the mapping
    const synonyms = multilingualKeywords[word];
    if (synonyms) {
      synonyms.forEach(syn => expandedTerms.add(syn));
    }
    
    // Also check if this word is a synonym of something else
    Object.entries(multilingualKeywords).forEach(([key, values]) => {
      if (values.includes(word)) {
        expandedTerms.add(key);
      }
    });
  });
  
  // Also check multi-word phrases
  Object.entries(multilingualKeywords).forEach(([key, values]) => {
    if (normalizedQuery.includes(key)) {
      expandedTerms.add(key);
      values.forEach(syn => expandedTerms.add(syn));
    }
    values.forEach(value => {
      if (normalizedQuery.includes(value)) {
        expandedTerms.add(key);
        values.forEach(syn => expandedTerms.add(syn));
      }
    });
  });
  
  return Array.from(expandedTerms);
};

// Suggestion interface
export interface StoreSuggestion {
  type: "store" | "city" | "country";
  text: string;
  subtext?: string;
  store?: AppleStoreType;
  count?: number;
}

// Get smart suggestions with fuzzy search
export const getSmartSuggestions = (query: string, maxResults: number = 8): StoreSuggestion[] => {
  const normalizedQuery = query.toLowerCase().trim();
  
  if (!normalizedQuery || normalizedQuery.length < 1) {
    return [];
  }
  
  // Expand the query with multilingual synonyms
  const expandedTerms = expandSearchQuery(normalizedQuery);
  
  const suggestions: StoreSuggestion[] = [];
  const addedStores = new Set<number>();
  const addedCities = new Set<string>();
  const addedCountries = new Set<string>();
  
  // Setup Fuse.js for fuzzy matching
  const fuseOptions = {
    keys: ["name", "city", "country", "address"],
    threshold: 0.4,
    includeScore: true,
    minMatchCharLength: 2,
  };
  
  const fuse = new Fuse(appleStores, fuseOptions);
  
  // First, find exact and fuzzy store matches
  const fuseResults = fuse.search(normalizedQuery);
  
  // Also search with expanded terms
  expandedTerms.forEach(term => {
    if (term !== normalizedQuery) {
      const termResults = fuse.search(term);
      termResults.forEach(result => {
        if (!fuseResults.find(r => r.item.id === result.item.id)) {
          fuseResults.push(result);
        }
      });
    }
  });
  
  // Sort by score
  fuseResults.sort((a, b) => (a.score || 0) - (b.score || 0));
  
  // Group stores by city and country
  const cityCounts: Record<string, { count: number; stores: AppleStoreType[] }> = {};
  const countryCounts: Record<string, { count: number; stores: AppleStoreType[] }> = {};
  
  fuseResults.forEach(result => {
    const store = result.item;
    const cityKey = store.city.toLowerCase();
    const countryKey = store.country.toLowerCase();
    
    if (!cityCounts[cityKey]) {
      cityCounts[cityKey] = { count: 0, stores: [] };
    }
    cityCounts[cityKey].count++;
    cityCounts[cityKey].stores.push(store);
    
    if (!countryCounts[countryKey]) {
      countryCounts[countryKey] = { count: 0, stores: [] };
    }
    countryCounts[countryKey].count++;
    countryCounts[countryKey].stores.push(store);
  });
  
  // Add city suggestions if they match well
  Object.entries(cityCounts).forEach(([city, data]) => {
    const cityMatches = expandedTerms.some(term => city.includes(term) || term.includes(city));
    if (cityMatches && data.count > 1 && !addedCities.has(city)) {
      suggestions.push({
        type: "city",
        text: data.stores[0].city,
        subtext: `${data.count} store in ${data.stores[0].country}`,
        count: data.count,
      });
      addedCities.add(city);
    }
  });
  
  // Add country suggestions if they match well
  Object.entries(countryCounts).forEach(([country, data]) => {
    const countryMatches = expandedTerms.some(term => country.includes(term) || term.includes(country));
    if (countryMatches && data.count > 2 && !addedCountries.has(country)) {
      suggestions.push({
        type: "country",
        text: data.stores[0].country,
        subtext: `${data.count} store`,
        count: data.count,
      });
      addedCountries.add(country);
    }
  });
  
  // Add individual store suggestions
  fuseResults.slice(0, maxResults - suggestions.length).forEach(result => {
    const store = result.item;
    if (!addedStores.has(store.id)) {
      suggestions.push({
        type: "store",
        text: store.name,
        subtext: `${store.city}, ${store.country}`,
        store: store,
      });
      addedStores.add(store.id);
    }
  });
  
  // Sort suggestions: cities first, then countries, then stores
  suggestions.sort((a, b) => {
    const typeOrder = { city: 0, country: 1, store: 2 };
    return typeOrder[a.type] - typeOrder[b.type];
  });
  
  return suggestions.slice(0, maxResults);
};

// Get all stores matching a city or country
export const getStoresByLocation = (type: "city" | "country", location: string): AppleStoreType[] => {
  const normalizedLocation = location.toLowerCase();
  
  if (type === "city") {
    return appleStores.filter(store => store.city.toLowerCase() === normalizedLocation);
  } else {
    return appleStores.filter(store => store.country.toLowerCase() === normalizedLocation);
  }
};
